import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';
import type {
  CaseListResponse,
  CaseDetailResponse,
  TradeCaseStatusResponse,
  SummonAdminResponse,
  CaseBadgeCountResponse,
  AdminActionResponse,
  SendAdminMessageResponse,
  SetDueDateResponse
} from 'types/interfaces/case';

export const caseApi = createApi({
  reducerPath: 'caseApi',
  baseQuery: baseQuery,
  tagTypes: ['Cases', 'CaseDetail', 'CaseBadgeCount'],
  endpoints: (builder) => ({
    // User-facing endpoints
    summonAdmin: builder.mutation<SummonAdminResponse, { tradeId: string }>({
      query: ({ tradeId }) => ({
        url: `/trades/${tradeId}/summon-admin`,
        method: 'POST',
        requiresAuth: true,
      }),
      invalidatesTags: ['Cases', 'CaseBadgeCount'],
    }),

    getTradeCase: builder.query<TradeCaseStatusResponse, string>({
      query: (tradeId) => ({
        url: `/trades/${tradeId}/case`,
        method: 'GET',
        requiresAuth: true,
      }),
    }),

    // Admin endpoints
    getCases: builder.query<CaseListResponse, {
      page?: number;
      pageSize?: number;
      status?: 'unresolved' | 'resolved' | 'all';
      search?: string;
      sortBy?: 'caseDate' | 'tradeDate';
      sortOrder?: 'asc' | 'desc';
    }>({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params.page) queryParams.append('page', params.page.toString());
        if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
        if (params.status) queryParams.append('status', params.status);
        if (params.search) queryParams.append('search', params.search);
        if (params.sortBy) queryParams.append('sortBy', params.sortBy);
        if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

        return {
          url: `/admin/cases?${queryParams.toString()}`,
          method: 'GET',
          requiresAuth: true,
        };
      },
      providesTags: ['Cases'],
    }),

    getCaseById: builder.query<CaseDetailResponse, string>({
      query: (caseId) => ({
        url: `/admin/cases/${caseId}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: (result, error, caseId) => [
        { type: 'CaseDetail', id: caseId },
        'Cases'
      ],
    }),

    getCaseBadgeCount: builder.query<CaseBadgeCountResponse, void>({
      query: () => ({
        url: '/admin/cases/badge/count',
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['CaseBadgeCount'],
    }),

    sendAdminMessage: builder.mutation<SendAdminMessageResponse, {
      caseId: string;
      content: string
    }>({
      query: ({ caseId, content }) => ({
        url: `/admin/cases/${caseId}/messages`,
        method: 'POST',
        body: { content },
        requiresAuth: true,
      }),
      invalidatesTags: (result, error, { caseId }) => [
        { type: 'CaseDetail', id: caseId },
        'Cases'
      ],
    }),

    performAdminAction: builder.mutation<AdminActionResponse, {
      caseId: string;
      actionType: string;
      details?: any;
    }>({
      query: ({ caseId, actionType, details }) => ({
        url: `/admin/cases/${caseId}/actions`,
        method: 'POST',
        body: { actionType, details },
        requiresAuth: true,
      }),
      invalidatesTags: (result, error, { caseId }) => [
        { type: 'CaseDetail', id: caseId },
        'Cases',
        'CaseBadgeCount'
      ],
    }),

    setDueDate: builder.mutation<SetDueDateResponse, {
      caseId: string;
      dueDate: string
    }>({
      query: ({ caseId, dueDate }) => ({
        url: `/admin/cases/${caseId}/due-date`,
        method: 'PUT',
        body: { dueDate },
        requiresAuth: true,
      }),
      invalidatesTags: (result, error, { caseId }) => [
        { type: 'CaseDetail', id: caseId },
        'Cases'
      ],
    }),
  }),
});

export const {
  useSummonAdminMutation,
  useGetTradeCaseQuery,
  useGetCasesQuery,
  useGetCaseByIdQuery,
  useGetCaseBadgeCountQuery,
  useSendAdminMessageMutation,
  usePerformAdminActionMutation,
  useSetDueDateMutation,
} = caseApi;
